<template>
	<v-card class="card-roles">
		<v-card-actions>
			<v-btn v-if="!editingRoleId" color="green" dark @click="$emit('role:create')">
				<v-icon>add</v-icon>
				Add Role
			</v-btn>

			<v-layout row wrap v-else>
				<v-flex xs8>
					<div class="d-flex align-center">
						<label for="enable-disable-role" class="mr-1" style="flex: 0 1 auto !important">Enable / Disable Role</label>
						<v-switch id="enable-disable-role" v-model="editingRole.enabled" hide-details class="ma-0 pa-0"></v-switch>
					</div>
					<v-text-field
						v-model="editingRole.name"
						label="Name"
						style="max-width: 300px"
						:rules="[validateRoleName]"
					></v-text-field>
					<v-text-field v-model="editingRole.description" label="Description"></v-text-field>
				</v-flex>

				<v-spacer></v-spacer>

				<v-flex xs3>
					<div class="text-xs-right">
						<v-btn color="primary" @click="handleUpdateRole" :disabled="!canSave">
							<v-icon>mdi-content-save</v-icon>
							Save
						</v-btn>
						<v-btn color="red" dark @click="handleCancelEditRole">
							<v-icon>close</v-icon>
							Cancel
						</v-btn>
					</div>
				</v-flex>
			</v-layout>
		</v-card-actions>

		<v-divider></v-divider>

		<v-card-text class="pa-0">
			<v-data-table
				:headers="tableHeaders"
				:items="flattenedItems"
				:hide-actions="true"
				:footer-props="{ itemsPerPageOptions: [] }"
				item-key="_rowKey"
				class="elevation-0"
				id="roles-permissions"
			>
				<template v-slot:headers="props">
					<tr>
						<th v-for="header in props.headers" :key="header.text">
							<v-tooltip bottom style="z-index: 50">
								<template v-slot:activator="{ on }">
									<span class="header-rolename" v-on="on">{{ header.text }}</span>
								</template>
								<div style="max-width: 300px">
									<p>
										<strong>{{ header.text }}</strong>
									</p>
									<span><strong>Description</strong>: {{ header.description }}</span>
								</div>
							</v-tooltip>

							<v-menu v-if="header.text !== '' && header.text !== 'Administrator' && !editingRoleId">
								<template v-slot:activator="{ on }">
									<v-btn icon flat style="margin: 0" small v-on="on">
										<v-icon small>more_vert</v-icon>
									</v-btn>
								</template>

								<v-list dense>
									<v-list-tile @click="handleEditRole(header.value)">
										<v-list-tile-title>
											<v-layout row align-center>
												<v-icon small class="mr-2">edit</v-icon>
												<span>Edit</span>
											</v-layout>
										</v-list-tile-title>
									</v-list-tile>

									<v-list-tile @click="$emit('role:duplicate', header.value)">
										<v-list-tile-title>
											<v-layout row align-center>
												<v-icon small class="mr-2">mdi-content-duplicate</v-icon>
												<span>Duplicate</span>
											</v-layout>
										</v-list-tile-title>
									</v-list-tile>

									<v-list-tile @click="$emit('role:delete', header.value)">
										<v-list-tile-title>
											<v-layout row align-center>
												<v-icon small class="mr-2">mdi-trash-can-outline</v-icon>
												<span>Delete</span>
											</v-layout>
										</v-list-tile-title>
									</v-list-tile>
								</v-list>
							</v-menu>
						</th>
					</tr>
				</template>

				<template slot="items" slot-scope="{ item }">
					<!-- fila de grupo -->
					<tr v-if="item.__isGroup">
						<td class="category-cell grey lighten-4 font-weight-bold" style="background: #f5f5f5">
							{{ format(item.category) }}
						</td>
						<td :colspan="roles.length" class="category-fill grey lighten-4" style="background: #f5f5f5"></td>
					</tr>

					<!-- fila de permiso -->
					<tr v-else>
						<!-- primera columna: permiso con tooltip -->
						<td>
							<div class="d-flex align-center">
								<v-tooltip bottom>
									<template v-slot:activator="{ on }">
										<div class="perm-text" v-on="on">
											<v-icon small class="mr-2 info-icon">mdi-information-outline</v-icon>
											<span class="perm-name">{{ item.name }}</span>
										</div>
									</template>
									<div><strong>Description</strong>: {{ item.description }}</div>
								</v-tooltip>
							</div>
						</td>

						<!-- celdas por cada role -->
						<td v-for="role in sortedRoles" :key="role._id + '_' + item._id" class="text-xs-center">
							<div v-if="!editingRoleId || editingRoleId !== role._id">
								<v-icon v-if="roleHasPermission(role, item)" color="green">check_circle</v-icon>
								<v-icon v-else color="red">cancel</v-icon>
							</div>

							<v-layout v-else row wrap align-center justify-center>
								<v-flex xs1>
									<v-checkbox
										v-model="editingRole.permissions"
										:value="item._id"
										color="green"
										hide-details
									></v-checkbox>
								</v-flex>
							</v-layout>
						</td>
					</tr>
				</template>
			</v-data-table>
		</v-card-text>

		<v-card-actions v-if="editingRoleId">
			<v-spacer></v-spacer>

			<div>
				<v-btn color="primary" @click="handleUpdateRole" :disabled="!canSave">
					<v-icon>mdi-content-save</v-icon>
					Save
				</v-btn>
				<v-btn color="red" dark @click="handleCancelEditRole">
					<v-icon>close</v-icon>
					Cancel
				</v-btn>
			</div>
		</v-card-actions>
	</v-card>
</template>

<script>
export default {
	name: 'RolesPermissionsMatrix',
	props: {
		roles: {
			type: Array,
			required: true,
			default: () => []
		},
		permissions: {
			type: Array,
			required: true,
			default: () => []
		}
	},
	data() {
		return {
			editingRoleId: null,
			editingRole: null
		};
	},
	computed: {
		sortedRoles() {
			let sortedRoles = [...this.roles];

			return sortedRoles.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
		},
		tableHeaders() {
			const first = { text: '', value: 'name', sortable: false, width: '320px' };
			const roleHeaders = this.sortedRoles.map(role => ({
				text: role.name,
				value: role._id,
				sortable: false,
				align: 'center',
				description: role.description,
				width: '160px'
			}));

			return [first, ...roleHeaders];
		},

		flattenedItems() {
			const map = {};
			const order = [];
			(this.permissions || []).forEach(p => {
				const cat = p.category || 'Uncategorized';
				if (!map[cat]) {
					map[cat] = [];
					order.push(cat);
				}
				map[cat].push(p);
			});

			const flat = [];
			let rowIndex = 0;
			for (const cat of order) {
				flat.push({ __isGroup: true, category: cat, _rowKey: `group_${cat}_${rowIndex++}` });
				for (const perm of map[cat]) {
					flat.push(Object.assign({}, perm, { __isGroup: false, _rowKey: `perm_${perm._id}_${rowIndex++}` }));
				}
			}
			return flat;
		},
		canSave() {
			return this.validateRoleName(this.editingRole.name) === true;
		}
	},
	watch: {
		roles: {
			immediate: false,
			handler() {
				if (!this.editingRoleId) return;

				this.cancelEditRole();
			}
		}
	},
	methods: {
		validateRoleName(roleName) {
			const exp = /^[A-Za-z0-9_-]+$/;

			return exp.test(roleName) || 'Name may only contain letters, numbers, - and _';
		},
		roleHasPermission(role, perm) {
			if (!role || !Array.isArray(role.permissions)) return false;
			return role.permissions.some(permissionId => {
				if (!permissionId) return false;

				return permissionId._id === perm._id;
			});
		},
		format(string) {
			const words = string.split('_');
			const formattedWords = words.map(word => word.charAt(0).toUpperCase() + word.slice(1));
			return formattedWords.join(' ');
		},
		handleEditRole(roleId) {
			this.editingRoleId = roleId;

			this.fillEditingRole(roleId);
		},
		fillEditingRole(roleId) {
			this.editingRole = Object.assign(
				{},
				this.roles.find(role => role._id === roleId)
			);
			this.editingRole.enabled = !this.editingRole.disabled;
			this.editingRole.permissions = this.editingRole.permissions.map(permission => permission._id);
		},
		cancelEditRole() {
			this.editingRoleId = null;
			this.editingRole = null;
		},
		handleCancelEditRole() {
			this.cancelEditRole();
		},
		handleUpdateRole() {
			this.editingRole.disabled = !this.editingRole.enabled;
			delete this.editingRole.enabled;

			const role = Object.assign({}, this.editingRole);
			this.$emit('role:update', role);
			this.cancelEditRole();
		}
	}
};
</script>

<style>
.card-roles {
	height: 100%;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.card-roles .v-card__text {
	flex: 1;
	overflow: hidden;
}

#roles-permissions {
	height: 100%;
}

#roles-permissions > * {
	box-sizing: border-box;
}

#roles-permissions .v-table__overflow {
	overflow: auto;
	height: 100%;
}

#roles-permissions table.v-datatable.v-table {
	table-layout: fixed;
	width: 100%;
	border-collapse: separate;
}

#roles-permissions table.v-datatable.v-table thead th {
	position: sticky;
	top: 0;
	z-index: 40;
	background: #f5f5f5;
	border-bottom: 1px solid #e0e0e0;
	padding: 12px 4px;
}

#roles-permissions table.v-datatable.v-table tbody td {
	border-bottom: 1px solid #f0f0f0;
	vertical-align: middle;
	padding: 10px 4px;
}

#roles-permissions table.v-datatable.v-table thead th:first-child,
#roles-permissions table.v-datatable.v-table tbody td:first-child {
	position: sticky;
	left: 0;
	z-index: 35;
	min-width: 320px;
	max-width: 320px;
	width: 320px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

#roles-permissions table.v-datatable.v-table tbody td:first-child {
	background: #fff;
}

#roles-permissions table.v-datatable.v-table thead th:first-child {
	z-index: 45;
}

#roles-permissions table.v-datatable.v-table thead th:nth-child(n + 2),
#roles-permissions table.v-datatable.v-table tbody td:nth-child(n + 2) {
	min-width: 120px;
	max-width: 160px;
	width: 160px;
	overflow: hidden;
}

#roles-permissions table.v-datatable.v-table thead th:nth-child(n + 2) .header-rolename {
	display: inline-block;
	width: 120px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	vertical-align: middle;
}

#roles-permissions .category-cell {
	min-width: 320px;
	max-width: 320px;
	width: 320px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	font-weight: 600;
}

.perm-text .perm-name {
	font-weight: 500;
}

.perm-code {
	font-size: 0.85rem;
}

.role-header {
	text-align: center;
}

.text-xs-center {
	text-align: center;
}

.v-icon {
	vertical-align: middle;
}
</style>
